import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, IconButton, Stack } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { SnackbarContext } from "components/snackBar/SnackbarContext";
import CustomPagination from "views/client/pagination";
import { ViewModuleOutlined, ViewListOutlined } from "@mui/icons-material";
import NewSearchBar from "components/searchBars/NewSearchBar";
import { useTranslation } from "react-i18next";
import { PostSearchBar } from "components/searchBars/SearchBars";
import CreateSuperAdminDialog from "../CreateSuperAdminDialog";
import EditAdminDialog from "../EditAdminDialog";
import AdminDetailsDialog from "../AdminDetailsDialog";
import ModernAdminCard from "../ModernAdminCard"; // Import the new card component
import searchIcon from "../../../assets/img/search_icon.svg";
import addIcon from "../../../assets/img/add_icon.svg";
import filterIcon from "../../../assets/img/filter_icon.svg";

// Midnight blue theme colors
const colors = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#0ea5e9',
  text: '#64748b',
  lightText: '#94a3b8',
  background: '#f8fafc',
};

const MainContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "transparent",
  minHeight: "100vh",
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  background: "linear-gradient(135deg, #ffffff 0%, #fafbff 100%)",
  border: `1px solid ${alpha(colors.primary, 0.08)}`,
  boxShadow: `0 8px 32px ${alpha(colors.primary, 0.08)}`,
}));

const ViewToggleButton = styled(IconButton)(({ theme, active }) => ({
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: active ? alpha(colors.primary, 0.1) : "transparent",
  color: active ? colors.primary : alpha(colors.primary, 0.5),
  border: `1px solid ${active ? colors.primary : alpha(colors.primary, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(colors.primary, 0.05),
    color: colors.primary,
  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
  gap: theme.spacing(3),
  padding: theme.spacing(1),
  backgroundColor: "transparent",
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
  },
}));

const ListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
}));

const SuperAdminCreateAdmin = () => {
  const { showSnackbar } = useContext(SnackbarContext);
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    image: '',
  });

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [viewMode, setViewMode] = useState('grid'); // Changed default to grid for better card display
  const [filter, setFilter] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { t } = useTranslation();

  const adminCompletionPercentage = useMemo(() => {
    const requiredFields = ['username', 'email', 'password', 'first_name', 'last_name'];
    const optionalFields = ['company', 'address', 'city', 'country', 'postal_code', 'image'];
    const filledRequired = requiredFields.filter(field => (form[field] || '').trim() !== '').length;
    const filledOptional = optionalFields.filter(field => (form[field] || '').trim() !== '').length;
    const requiredPercentage = (filledRequired / requiredFields.length) * 70;
    const optionalPercentage = (filledOptional / optionalFields.length) * 30;
    return Math.round(requiredPercentage + optionalPercentage);
  }, [form]);

  const adminFieldsConfig = useMemo(() => ([
    {
      section: t('superAdmin.screens.createAdmin.accountInformation'),
      icon: null,
      fields: [
        { name: 'username', label: t('superAdmin.screens.createAdmin.username'), required: true },
        { name: 'email', label: t('superAdmin.screens.createAdmin.email'), required: true, type: 'email' },
        { name: 'password', label: t('superAdmin.screens.createAdmin.password'), required: true, type: 'password' }
      ]
    },
    {
      section: t('superAdmin.screens.createAdmin.personalDetails'),
      icon: null,
      fields: [
        { name: 'first_name', label: t('superAdmin.screens.createAdmin.firstName'), required: true },
        { name: 'last_name', label: t('superAdmin.screens.createAdmin.lastName'), required: true },
        { name: 'company', label: t('superAdmin.screens.createAdmin.company') }
      ]
    },
    {
      section: t('superAdmin.screens.createAdmin.locationAndContact'),
      icon: null,
      fields: [
        { name: 'address', label: t('superAdmin.screens.createAdmin.address') },
        { name: 'city', label: t('superAdmin.screens.createAdmin.city') },
        { name: 'country', label: t('superAdmin.screens.createAdmin.country') },
        { name: 'postal_code', label: t('superAdmin.screens.createAdmin.postalCode') }
      ]
    },
    {
      section: t('superAdmin.screens.createAdmin.profileImage'),
      icon: null,
      fields: [
        { name: 'image', label: t('superAdmin.screens.createAdmin.imageUrl') }
      ]
    }
  ]), [t]);

  const normalizeUser = (u) => ({
    ...u,
    username: u?.username ?? u?.user_name ?? u?.user?.username ?? '',
    postal_code: u?.postal_code ?? u?.postalCode ?? u?.zip_code ?? u?.zip ?? u?.address?.postal_code ?? ''
  });

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');

      // Only send fields with non-empty values to avoid backend validation on empty strings
      const payload = Object.entries(form).reduce((acc, [key, value]) => {
        const trimmed = typeof value === 'string' ? value.trim() : value;
        if (trimmed !== '' && trimmed !== null && trimmed !== undefined) acc[key] = trimmed;
        return acc;
      }, {});

      const res = await fetch(`${apiUrl}authentication/superadmin/create-admin/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      let errorDetail = '';
      if (!res.ok) {
        try {
          const data = await res.json();
          // Collect readable error messages
          if (data) {
            if (typeof data === 'string') errorDetail = data;
            else if (Array.isArray(data)) errorDetail = data.join(', ');
            else if (data.detail) errorDetail = data.detail;
            else {
              errorDetail = Object.entries(data)
                .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                .join(' | ');
            }
          }
          // Log full payload+response for debugging
          console.error('Create admin failed', { status: res.status, payload, data });
        } catch (_) {
          console.error('Create admin failed', { status: res.status });
        }
        throw new Error(errorDetail || 'Failed to create admin');
      }

      showSnackbar && showSnackbar('Admin created successfully', { severity: 'success' });
      setForm({ username: '', email: '', password: '', first_name: '', last_name: '', company: '', address: '', city: '', country: '', postal_code: '', image: '' });
      fetchAdmins();
      return true;
    } catch (e) {
      console.error(e);
      showSnackbar && showSnackbar(e.message || 'Error creating admin', { severity: 'error' });
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiUrl}authentication/superadmin/admins/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const list = Array.isArray(data?.results || data) ? (data.results || data) : [];
      const normalized = list.map(normalizeUser);
      setAdmins(normalized);
      setPage(0);
    } catch (e) {
      console.error(e);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAdmin = (admin) => {
    setSelectedItem(admin);
    setDetailsOpen(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedItem(admin);
    setEditOpen(true);
  };

  const filteredAdmins = admins.filter(a => 
    `${a.first_name || ''} ${a.last_name || ''} ${a.email || ''}`.toLowerCase().includes(filter.toLowerCase())
  );

  const paginatedAdmins = filteredAdmins.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    fetchAdmins();
  }, []);

  return (
    <MainContainer>
      
      
      <Box p={3} sx={{ pt: 0 }}>
        <Box sx={{ mb: 2 }}>
          <NewSearchBar
            admin={true}
            filter={filter}
            setFilter={setFilter}
            setOpen={setOpenCreate}
            t={t}
            searchIcon={searchIcon}
            addIcon={addIcon}
            filterIcon={filterIcon}
            PostSearchBar={PostSearchBar}
            handleSearch={() => {}}
            addLabel={t('postPageMeta.addAdminButton')}
            searchPlaceholder={t('postPageMeta.filterAdmin')}
            searchLabel={t('postPage.search')}
          />
        </Box>

        <CreateSuperAdminDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          form={form}
          setForm={setForm}
          onSubmit={async () => { const ok = await handleSubmit(); if (ok) setOpenCreate(false); }}
          submitLoading={submitLoading}
          completion={adminCompletionPercentage}
          title={t('superAdmin.screens.createAdmin.createAdmin')}
          subtitle={t('superAdmin.screens.createAdmin.addNewAdministrator')}
          actionLabel={t('superAdmin.screens.createAdmin.createAdminAction')}
          fieldsConfig={adminFieldsConfig}
        />

        <AdminDetailsDialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          item={selectedItem}
          userType="admin"
        />

        <EditAdminDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          item={selectedItem}
          userType="admin"
          onUpdated={() => { setEditOpen(false); fetchAdmins(); }}
        />

        <Box mt={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: colors.primary }}>
              {t('superAdmin.screens.createAdmin.existingAdmins')} ({filteredAdmins.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ViewToggleButton active={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
                <ViewModuleOutlined fontSize="small" />
              </ViewToggleButton>
              <ViewToggleButton active={viewMode === 'list'} onClick={() => setViewMode('list')}>
                <ViewListOutlined fontSize="small" />
              </ViewToggleButton>
            </Box>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography sx={{ color: colors.text, fontFamily: 'Poppins' }}>{t('superAdmin.screens.createAdmin.loading')}</Typography>
            </Box>
          ) : filteredAdmins.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography sx={{ color: colors.text, fontFamily: 'Poppins' }}>{t('superAdmin.screens.createAdmin.noAdminsFound')}</Typography>
            </Box>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <GridContainer>
                  {paginatedAdmins.map((admin) => (
                    <ModernAdminCard
                      key={admin.id}
                      item={admin}
                      onView={handleViewAdmin}
                      onEdit={handleEditAdmin}
                      userType="admin"
                      showCreatedBy={true}
                    />
                  ))}
                </GridContainer>
              ) : (
                <ListContainer>
                  {paginatedAdmins.map((admin) => (
                    <ModernAdminCard
                      key={admin.id}
                      item={admin}
                      onView={handleViewAdmin}
                      onEdit={handleEditAdmin}
                      userType="admin"
                      showCreatedBy={true}
                    />
                  ))}
                </ListContainer>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <CustomPagination
                  page={page}
                  handleChangePage={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  count={filteredAdmins.length}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </MainContainer>
  );
};

export default SuperAdminCreateAdmin;